import Phaser from 'phaser';
import { ChipStackVisual } from './ChipStackVisual.js';
import { createChipSprite, decomposeBet } from './Chip.js';
import type { ChipBreakdownEntry } from './Chip.js';
import { scaleDuration } from '../animations.js';

const MAX_BURST_CHIPS = 8;
const SCATTER_RADIUS_MIN_PX = 30;
const SCATTER_RADIUS_MAX_PX = 60;
const SCATTER_DURATION_MS = 150;
const CONVERGE_DURATION_MS = 500;
const STAGGER_MAX_MS = 80;

/**
 * Flatten a chip decomposition into a per-chip array (largest denoms first),
 * capped at `max`. Used by the burst path to pick which decomposed chips
 * actually fly when a payout's full decomposition would be too many to
 * render. Pure helper; exported for unit testing.
 */
export function pickBurstChips(
  decomp: ChipBreakdownEntry[],
  max: number,
): number[] {
  const out: number[] = [];
  // ChipBreakdownEntry is already largest-first from decomposeBet, but sort
  // defensively so this helper's contract isn't coupled to its caller.
  const sorted = [...decomp].sort((a, b) => b.denom - a.denom);
  for (const entry of sorted) {
    for (let i = 0; i < entry.count; i++) {
      if (out.length >= max) return out;
      out.push(entry.denom);
    }
  }
  return out;
}

export interface FlyChipsOpts {
  scene: Phaser.Scene;
  /** Origin point (typically just left of the winning bet's chip stack). */
  from: { x: number; y: number };
  /** Destination (the bankroll pill anchor in viewport coords). */
  to: { x: number; y: number };
  /** Winnings amount in cents — drives the chip-decomposition + badge text. */
  cents: number;
  /** Visible chip radius in canvas-buffer px. */
  chipRadius?: number;
  /** Multiplier from intended-CSS-px to canvas-buffer-px. */
  dpr?: number;
  /** Tween duration. Defaults to 700ms. */
  durationMs?: number;
  /** Z-depth for the flying chips. Defaults above most table objects. */
  depth?: number;
  /** When true, decompose the payout and animate up to MAX_BURST_CHIPS
   *  individual chips that scatter outward then converge into `to`.
   *  When false/omitted, runs the existing single-stack tween. */
  burst?: boolean;
}

/**
 * Spawn a temporary chip stack at `from`, sail it to `to`, and tear it down
 * on arrival. Returns a Promise that resolves when the animation finishes —
 * callers (bridges) typically `Promise.all` several flies and then update
 * the bankroll display in lockstep with the chips landing.
 *
 * No-op (and immediately resolves) when `cents <= 0`.
 */
export function flyWinningChips(opts: FlyChipsOpts): Promise<void> {
  if (opts.cents <= 0) return Promise.resolve();
  if (opts.burst) return runBurst(opts);

  // Existing single-stack path (unchanged).
  const dpr = opts.dpr ?? 1;
  const stack = new ChipStackVisual({
    scene: opts.scene,
    anchor: opts.from,
    chipRadius: opts.chipRadius ?? Math.round(14 * dpr),
    dpr,
    depth: opts.depth ?? 100,
    // Fly chips skip the dollar badge — it'd clip off the right edge during
    // the tween, and the bankroll pill is the canonical "this much landed"
    // readout once the chips arrive.
    showBadge: false,
  });
  stack.setCents(opts.cents);
  return stack.tweenTo(opts.to.x, opts.to.y, opts.durationMs ?? 700)
    .then(() => stack.destroy());
}

function runBurst(opts: FlyChipsOpts): Promise<void> {
  const dpr = opts.dpr ?? 1;
  const chipRadius = opts.chipRadius ?? Math.round(14 * dpr);
  const depth = opts.depth ?? 100;

  // decomposeBet may return [] for amounts smaller than the minimum
  // denomination — defensively render at least one chip so the burst is
  // visible. Picks the smallest denomination so it reads as "small win".
  const decomp: ChipBreakdownEntry[] = decomposeBet(opts.cents);
  const denoms = decomp.length > 0
    ? pickBurstChips(decomp, MAX_BURST_CHIPS)
    : [100];

  return new Promise<void>((resolve) => {
    let landed = 0;
    const total = denoms.length;

    for (const denom of denoms) {
      const sprite = createChipSprite(opts.scene, denom, chipRadius);
      sprite.setPosition(opts.from.x, opts.from.y);
      sprite.setDepth(depth);

      const angle = Math.random() * Math.PI * 2;
      const radius = SCATTER_RADIUS_MIN_PX + Math.random() * (SCATTER_RADIUS_MAX_PX - SCATTER_RADIUS_MIN_PX);
      const scatterX = opts.from.x + Math.cos(angle) * radius * dpr;
      const scatterY = opts.from.y + Math.sin(angle) * radius * dpr;
      const startDelay = scaleDuration(Math.random() * STAGGER_MAX_MS);

      opts.scene.tweens.add({
        targets: sprite,
        x: scatterX,
        y: scatterY,
        duration: scaleDuration(SCATTER_DURATION_MS),
        delay: startDelay,
        ease: 'Quadratic.Out',
        onComplete: () => {
          opts.scene.tweens.add({
            targets: sprite,
            x: opts.to.x,
            y: opts.to.y,
            duration: scaleDuration(CONVERGE_DURATION_MS),
            ease: 'Cubic.In',
            onComplete: () => {
              sprite.destroy();
              landed += 1;
              if (landed === total) resolve();
            },
          });
        },
      });
    }
  });
}
